import React, { useEffect, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { flushSync } from 'react-dom';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { useSwipeable } from 'react-swipeable';

import { togglePopup } from 'apps/auth/actions/appAction';

import { Overlay, PopupBox, PopupBoxInner, PopupOuter } from './styles';

type AppStateSlice = {
    isShowSpinner?: boolean;
    isShowPopup?: boolean;
    popupComponent?: React.ReactNode;
    canClose?: boolean;
};

type ReduxState = {
    app: AppStateSlice;
};

const mapStateToProps = (state: ReduxState) => ({
    isShowSpinner: Boolean(state.app?.isShowSpinner),
    isShowPopup: Boolean(state.app?.isShowPopup),
    component: state.app?.popupComponent ?? null,
    canClose: state.app?.canClose ?? true,
});

const connector = connect(mapStateToProps, { togglePopup });

type PopupProps = ConnectedProps<typeof connector> & {
    children?: React.ReactNode;
};

type PopupBoxProps = ComponentProps<typeof PopupBox>;
type PopupBoxInnerProps = ComponentProps<typeof PopupBoxInner>;

const AnimatedPopupBox = PopupBox as React.ComponentType<PopupBoxProps & { animated?: boolean }>;
const SpinnerPopupBoxInner = PopupBoxInner as React.ComponentType<PopupBoxInnerProps & { isShowSpinner?: boolean }>;

type PopupState = {
    isShow: boolean;
    component: React.ReactNode | null;
    animationEnd: boolean;
};

const closePopup = (event: Event | React.MouseEvent, canClose: boolean, toggle: (value: boolean) => void | Promise<void>) => {
    if (__isBrowser__ && canClose) {
        event.preventDefault();
        void toggle(false);
    }
};

const PopupComponent = ({ canClose, togglePopup: togglePopupAction, isShowPopup, component, isShowSpinner, children }: PopupProps) => {
    const popupRef = useRef<HTMLDivElement>(null);
    const [state, setState] = useState<PopupState>({
        isShow: false,
        component: null,
        animationEnd: false,
    });

    useEffect(() => {
        if (!canClose) return;

        const keyPressHandler = (event: KeyboardEvent) => {
            if (event.key === 'Escape' || event.keyCode === 27) {
                closePopup(event, canClose, togglePopupAction);
            }
        };

        document.addEventListener('keydown', keyPressHandler);

        return () => {
            document.removeEventListener('keydown', keyPressHandler);
        };
    }, [canClose, togglePopupAction]);

    useEffect(() => {
        if (isShowPopup) {
            setState((prevState) => ({
                ...prevState,
                isShow: true,
                component,
            }));
        } else if (!isShowPopup && component) {
            setState((prevState) => ({
                ...prevState,
                animationEnd: true,
            }));
        }
    }, [isShowPopup, component]);

    const slide = () => {
        if (canClose) void togglePopupAction(false);
    };

    const handlers = useSwipeable({
        onSwipedDown: slide,
        delta: 20,
        preventScrollOnSwipe: true,
        trackTouch: true,
    });

    const overlayClick = (event: React.MouseEvent) => {
        if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
            closePopup(event, canClose, togglePopupAction);
        }
    };

    const closeAnimationPopup = () => {
        if (!isShowPopup) {
            flushSync(() => {
                setState({
                    isShow: false,
                    component: null,
                    animationEnd: false,
                });
            });
        }
    };

    if (!state.isShow) {
        return null;
    }

    return (
        <div id="popup-body" {...handlers}>
            {children}
            <Overlay onClick={overlayClick}>
                <PopupOuter>
                    <AnimatedPopupBox animated={!isShowPopup && state.animationEnd} onAnimationEnd={closeAnimationPopup}>
                        <SpinnerPopupBoxInner ref={popupRef} isShowSpinner={isShowSpinner}>
                            {state.component}
                        </SpinnerPopupBoxInner>
                    </AnimatedPopupBox>
                </PopupOuter>
            </Overlay>
        </div>
    );
};

export default connector(PopupComponent);
